// 第三方库
import React from 'react';
import { Modal, Card, Typography, Space, Button, message } from 'antd';
import { observer } from 'mobx-react-lite';
import { ReloadOutlined } from '@ant-design/icons';

// 内部模块 - Store
import { tokenDebugStore } from '@/store/TokenDebugStore';
import { accountStore } from '@/store/AccountStore';
import { useUserInfo } from '@/hooks/useUserInfo';

const { Text } = Typography;

/**
 * Token 调试面板组件
 * 单一职责：显示 Token 调试信息
 */
const TokenDebugPanel = observer(() => {
  const userInfo = useUserInfo();

  // 处理刷新 Token
  const handleRefreshToken = async () => {
    await tokenDebugStore.refreshToken();
    if (tokenDebugStore.lastMessage?.type === 'success') {
      message.success('Token 刷新成功！');
    } else {
      message.error(tokenDebugStore.lastMessage?.content || 'Token 刷新失败');
    }
  };

  // 处理关闭
  const handleClose = () => {
    tokenDebugStore.hide();
  };

  return (
    <Modal
      title='Token 调试面板'
      open={tokenDebugStore.visible}
      onCancel={handleClose}
      footer={null}
      width={800}
      style={{ top: 20 }}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='large'>
        {/* Token 信息卡片 */}
        <Card title='当前 Token 信息' size='small'>
          <Space direction='vertical' style={{ width: '100%' }}>
            <div>
              <Text strong>Token 状态: </Text>
              <Text type={accountStore.erpUserInfo?.token ? 'success' : 'danger'}>
                {accountStore.erpUserInfo?.token ? '有效' : '无效/缺失'}
              </Text>
            </div>
            <div>
              <Text strong>Token: </Text>
              <Text code copyable>
                {tokenDebugStore.formatToken(accountStore.erpUserInfo?.token)}
              </Text>
            </div>
            <div>
              <Text strong>完整 Token: </Text>
              <Text code copyable style={{ wordBreak: 'break-all' }}>
                {accountStore.erpUserInfo?.token || '无'}
              </Text>
            </div>
            <div>
              <Text strong>租户ID: </Text>
              <Text>{accountStore.erpUserInfo?.tenantId || '无'}</Text>
            </div>
            <div>
              <Text strong>用户名: </Text>
              <Text>{accountStore.erpUserInfo?.username || '无'}</Text>
            </div>
          </Space>
        </Card>

        {/* Token 解析信息 */}
        {accountStore.erpUserInfo?.token && (
          <Card title='Token 解析信息' size='small'>
            {(() => {
              const tokenInfo = tokenDebugStore.parseTokenInfo(accountStore.erpUserInfo.token);
              if (!tokenInfo) {
                return <Text type='danger'>Token 解析失败</Text>;
              }
              return (
                <Space direction='vertical' style={{ width: '100%' }}>
                  <div>
                    <Text strong>用户ID: </Text>
                    <Text>{tokenInfo.id}</Text>
                  </div>
                  <div>
                    <Text strong>用户名: </Text>
                    <Text>{tokenInfo.name}</Text>
                  </div>
                  <div>
                    <Text strong>租户ID: </Text>
                    <Text>{tokenInfo.tenantId}</Text>
                  </div>
                  <div>
                    <Text strong>管理员: </Text>
                    <Text type={tokenInfo.isAdmin ? 'success' : 'default'}>
                      {tokenInfo.isAdmin ? '是' : '否'}
                    </Text>
                  </div>
                  <div>
                    <Text strong>签发时间: </Text>
                    <Text>{tokenInfo.iat}</Text>
                  </div>
                  <div>
                    <Text strong>过期时间: </Text>
                    <Text type={tokenInfo.remainingTime < 30 ? 'danger' : 'default'}>
                      {tokenInfo.exp}
                    </Text>
                  </div>
                  <div>
                    <Text strong>剩余时间: </Text>
                    <Text type={tokenInfo.remainingTime < 30 ? 'danger' : 'default'}>
                      {tokenInfo.remainingTime} 分钟
                    </Text>
                  </div>
                </Space>
              );
            })()}
          </Card>
        )}

        {/* 用户信息卡片 */}
        <Card title='用户信息' size='small'>
          <Space direction='vertical' style={{ width: '100%' }}>
            <div>
              <Text strong>用户ID: </Text>
              <Text>{userInfo?.id || '无'}</Text>
            </div>
            <div>
              <Text strong>用户名: </Text>
              <Text>{userInfo?.name || '无'}</Text>
            </div>
            <div>
              <Text strong>租户ID: </Text>
              <Text>{userInfo?.tenantId || '无'}</Text>
            </div>
            <div>
              <Text strong>管理员: </Text>
              <Text type={userInfo?.isAdmin ? 'success' : 'default'}>
                {userInfo?.isAdmin ? '是' : '否'}
              </Text>
            </div>
            <div>
              <Text strong>状态: </Text>
              <Text type={userInfo?.status === 'active' ? 'success' : 'default'}>
                {userInfo?.status || '无'}
              </Text>
            </div>
          </Space>
        </Card>

        {/* 消息信息 */}
        {tokenDebugStore.lastMessage && (
          <Card
            title={tokenDebugStore.lastMessage.type === 'success' ? '操作结果' : '错误信息'}
            size='small'
            style={{
              borderColor: tokenDebugStore.lastMessage.type === 'success' ? '#52c41a' : '#ff4d4f',
            }}
          >
            <Text
              type={tokenDebugStore.lastMessage.type === 'success' ? 'success' : 'danger'}
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {tokenDebugStore.lastMessage.content}
            </Text>
          </Card>
        )}

        {/* 操作按钮 */}
        <Card title='操作' size='small'>
          <Space>
            <Button
              type='primary'
              icon={<ReloadOutlined />}
              onClick={handleRefreshToken}
              loading={tokenDebugStore.refreshing}
            >
              刷新 Token
            </Button>
            <Button onClick={handleClose}>关闭</Button>
          </Space>
        </Card>
      </Space>
    </Modal>
  );
});

export default TokenDebugPanel;
